import { Pill } from "./cells";
import { SortHeading, sortItems, useTableSort } from "./sorting";

/**
 * Net revenue retention, answering Raph's question in the terms he asked it.
 *
 *   "On 1st July we have 50 Candid clients generating 500k. What proportion
 *    have we lost by unit (# and %) and by revenue (£ and %), and how much did
 *    those 50 spend in the following 12 months."
 *
 * Loss is shown both ways deliberately: losing half your clients and three
 * quarters of your revenue are different stories, and only showing one hides
 * which happened.
 */
export default function NrrTable({
  groups: unsorted = [],
  totals,
  periodLabel,
  periodComplete = true,
  elapsedMonths = 12,
  onGroupClick,
  savedSort,
  onSortChange,
}) {
  const [sort, toggleSort] = useTableSort(savedSort, onSortChange);
  if (!unsorted.length) {
    return (
      <p className="py-12 text-center text-sm text-[#4a5b68]">
        No clients in this cohort.
      </p>
    );
  }

  const money = (value) =>
    new Intl.NumberFormat("en-GB", {
      style: "currency", currency: "GBP", maximumFractionDigits: 0,
    }).format(value || 0);
  const percent = (value) =>
    value === null || value === undefined ? "—" : `${value}%`;

  const tone = (nrr) =>
    nrr === null || nrr === undefined
      ? "text-[#a9b3bb]"
      : nrr >= 100
        ? "text-emerald-600"
        : nrr >= 90
          ? "text-amber-600"
          : "text-red-600";

  // The span is named rather than assumed to be a year: mid-year this measures
  // the months that have happened, and a partial figure labelled "12 months"
  // is a partial figure read as a full year.
  const headings = [
    { column: "label", label: "Product", get: (g) => g.label },
    { column: "base_units", label: "Clients", get: (g) => g.base_units, numeric: true },
    { column: "lost_units", label: "Lost", get: (g) => g.lost_units, numeric: true },
    { column: "base_revenue", label: "Opening revenue (a year)", get: (g) => g.base_revenue, numeric: true },
    { column: "lost_revenue", label: "Revenue lost", get: (g) => g.lost_revenue, numeric: true },
    {
      column: "following_revenue",
      label: periodComplete ? "12 months spend" : "Spend so far",
      get: (g) => g.following_revenue,
      numeric: true,
    },
    { column: "nrr", label: "NRR", get: (g) => g.nrr, numeric: true },
  ];
  const active = headings.find((h) => h.column === sort?.column);
  const groups = active ? sortItems(unsorted, active.get, sort.direction) : unsorted;

  const row = (group, isTotal = false) => (
    <tr
      key={group.label}
      onClick={() => !isTotal && onGroupClick && onGroupClick(group.label)}
      className={`${isTotal ? "border-t-2 border-[#cccccc] font-semibold" : "border-b border-[#efefef] last:border-0"} ${
        !isTotal && onGroupClick ? "cursor-pointer hover:bg-[#f5f5f5]" : ""
      }`}
    >
      <td className="px-3 py-2.5 font-medium text-[#323c44]">{group.label}</td>
      <td className="px-3 py-2.5 text-right">
        <Pill>{group.base_units}</Pill>
      </td>
      <td className="px-3 py-2.5 text-right">
        <Pill tone={group.lost_units ? "bad" : "neutral"}>{group.lost_units}</Pill>
        <span className="ml-1.5 text-[11px] text-[#7d8b96]">{percent(group.lost_units_pct)}</span>
      </td>
      <td className="px-3 py-2.5 text-right">
        <Pill tone="money">{money(group.base_revenue)}</Pill>
      </td>
      <td className="px-3 py-2.5 text-right">
        <Pill tone={group.lost_revenue ? "bad" : "neutral"}>{money(group.lost_revenue)}</Pill>
        <span className="ml-1.5 text-[11px] text-[#7d8b96]">{percent(group.lost_revenue_pct)}</span>
      </td>
      <td className="px-3 py-2.5 text-right">
        <Pill tone="money">{money(group.following_revenue)}</Pill>
        {/* What the opening run rate would have earned over the same months,
            which is what the NRR divides by. */}
        {!periodComplete && (
          <span className="ml-1.5 text-[11px] text-[#7d8b96]">
            of {money(group.expected_revenue)}
          </span>
        )}
      </td>
      <td className="px-3 py-2.5 text-right">
        <Pill
          tone={
            group.nrr === null || group.nrr === undefined
              ? "neutral"
              : group.nrr >= 100
                ? "good"
                : group.nrr >= 90
                  ? "warn"
                  : "bad"
          }
        >
          {percent(group.nrr)}
        </Pill>
      </td>
    </tr>
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[720px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-[#cccccc]">
            {headings.map((heading, index) => (
              <SortHeading
                key={heading.column}
                label={heading.label}
                column={heading.column}
                sort={sort}
                onToggle={toggleSort}
                numeric={heading.numeric}
                align={index === 0 ? "text-left" : "text-right"}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => row(group))}
          {totals && row({ label: "Total", ...totals }, true)}
        </tbody>
      </table>
      {!periodComplete && periodLabel && (
        <p className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-800">
          Covering {periodLabel}. NRR compares what these clients have spent so far with what
          their opening run rate would have earned over the same {elapsedMonths} month
          {elapsedMonths === 1 ? "" : "s"}, so it is comparable now and the spend grows as the
          year runs.
        </p>
      )}

    </div>
  );
}
